// Freshness rules for the session API key, kept pure so they can be tested without standing up
// AuthService and its Firebase dependencies.
//
// The platform mints API keys with a fixed server-side lifetime and returns the expiry alongside the
// key. The client used to store only the key, which made an expired one indistinguishable from a
// live one: every call kept sending a credential the server had already stopped accepting, and the
// regeneration path was unreachable because it only ran when the cache was EMPTY, never when it was
// STALE. These helpers decide when a cached key must be retired.

// Retire a key slightly before the server does. Covers two cases: a request already in flight when
// the key expires, and a modest clock difference between the browser and the server.
export const API_KEY_EXPIRY_MARGIN_MS = 5 * 60 * 1000;

// Whether a cached key is still usable.
//
// `expiresAtRaw` is the stored ISO timestamp, or null when none was recorded. A MISSING or
// unparseable stamp counts as NOT fresh on purpose: keys stored before the expiry became a
// persisted field carry no stamp, and a key of unknown age is exactly what this guards against.
// Being wrong in that direction costs one extra mint, which is cheap and does not revoke the
// existing key; being wrong the other way reinstates the original bug.
export function isKeyFresh(
  expiresAtRaw: string | null | undefined,
  now: number,
  marginMs: number = API_KEY_EXPIRY_MARGIN_MS
): boolean {
  if (!expiresAtRaw) {
    return false;
  }
  const expiresAt = Date.parse(expiresAtRaw);
  if (isNaN(expiresAt)) {
    return false;
  }
  return now < expiresAt - marginMs;
}

// Which expiry stamp to persist when saving a key.
//
// Returns the stamp to write, or null to REMOVE any stored stamp. Three cases:
//   - an expiry was supplied -> store it
//   - no expiry, but the same key is being re-saved -> keep the stamp it already has. Several
//     callers re-save the key the mint path just stored; wiping the stamp there would mark a
//     brand-new key stale and send the next reader straight back to minting another one.
//   - no expiry and a DIFFERENT key -> remove the stamp, so the new key cannot inherit the old
//     key's lifetime. isKeyFresh then treats it as stale and the first reader re-mints once.
export function expiryToPersist(
  newKey: string,
  previousKey: string | null | undefined,
  previousExpiry: string | null | undefined,
  expiresAt?: string
): string | null {
  if (expiresAt) {
    return expiresAt;
  }
  if (newKey && newKey === previousKey) {
    return previousExpiry || null;
  }
  return null;
}
